import { Dispatch, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { phoneNumberRegex } from 'common/components/utils/InputUtils'

import { Alert, Col, Input, Modal, Row, Skeleton } from 'common/components'
import { useLoginUserV2 } from 'common/hooks'

type SubmitToAgentModalProps = {
  isVisible: boolean
  setIsVisible: Dispatch<boolean>
}

const agencyName = 'TC Benefit Group'

const SubmitToAgentModal = ({ ...props }: SubmitToAgentModalProps) => {
  const { t: translate } = useTranslation()
  const { user } = useLoginUserV2()

  const [initialValues, setInitialValues] = useState<FormData | null>()

  useEffect(() => {
    if (user) {
      setInitialValues({
        fullName: `${user.firstName} ${user.lastName}`,
        email: user.email,
        mobilePhone: '',
        zip: '',
      })
    }
  }, [user])

  return (
    <Modal
      visible={props.isVisible}
      onOk={() => props.setIsVisible(false)}
      onCancel={() => props.setIsVisible(false)}
      title={translate('admin-dashboard.shop-for-policy.submit-to-agent.modal-title')}
      okText={translate('admin-dashboard.shop-for-policy.submit-to-agent.modal-ok').replace('AGENCY_NAME', agencyName)}
    >
      <h4 className="mb-16 semi-bold">{translate('admin-dashboard.shop-for-policy.submit-to-agent.title')}</h4>
      <p className="mb-24 gray-8">
        {translate('admin-dashboard.shop-for-policy.submit-to-agent.description').replace('AGENCY_NAME', agencyName)}
      </p>

      <Skeleton active={true} loading={!initialValues}>
        {initialValues && (
          <Formik
            enableReinitialize={true}
            validationSchema={formValidator(translate)}
            initialValues={initialValues}
            onSubmit={() => {}}
          >
            {(formik) => (
              <form onSubmit={formik.handleSubmit} autoComplete="off">
                <Row>
                  <Col span={24} className="mb-16">
                    <Input
                      label={translate('admin-dashboard.shop-for-policy.submit-to-agent.full-name-label')}
                      name="fullName"
                      disabled
                    />
                  </Col>
                  <Col span={24} className="mb-16">
                    <Input
                      label={translate('admin-dashboard.shop-for-policy.submit-to-agent.email-label')}
                      name="email"
                      disabled
                    />
                  </Col>
                  <Col span={24} className="mb-16">
                    <Input
                      label={translate('admin-dashboard.shop-for-policy.submit-to-agent.mobile-phone-label')}
                      placeholder="ex. (555) 123-4567"
                      name="mobilePhone"
                    />
                  </Col>
                  <Col span={24} className="mb-16">
                    <Input
                      label={translate('admin-dashboard.shop-for-policy.submit-to-agent.zip-code-label')}
                      placeholder="#####"
                      name="zip"
                    />
                  </Col>

                  <Col span={24}>
                    <h5 className="mb-24 semi-bold">
                      {translate('admin-dashboard.shop-for-policy.submit-to-agent.allowance-amount')
                        .replace('PRODUCT_NAME', 'QSEHRA')
                        .replace('ALLOWANCE_AMOUNT', '$200.00')}
                    </h5>
                  </Col>
                  <Col span={24}>
                    <Alert
                      showIcon
                      type="info"
                      message={''}
                      description={translate('admin-dashboard.shop-for-policy.submit-to-agent.disclaimer')}
                    />
                  </Col>
                </Row>
              </form>
            )}
          </Formik>
        )}
      </Skeleton>
    </Modal>
  )
}

export default SubmitToAgentModal

const formValidator = (translate: any) =>
  Yup.object({
    mobilePhone: Yup.string()
      .nullable()
      .matches(phoneNumberRegex, translate('admin-signup.company-details.errors.phone-number-regex'))
      .when('receiveSms', {
        is: (receiveSms: boolean) => receiveSms,
        then: Yup.string().required(' '),
      }),
    zip: Yup.string().nullable().required(translate('general.required')),
  })

type FormData = {
  fullName: string
  email: string
  mobilePhone: string
  zip?: number | ''
}
